import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
  Box
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import EventIcon from "@material-ui/icons/Event";
import PaymentIcon from "@material-ui/icons/Payment";
import { alpha, fade, makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const safeAlpha = alpha || fade;

const useStyles = makeStyles((theme) => ({
  // Banner na página (quando modal está fechado)
  bannerCard: {
    padding: theme.spacing(2, 2.5),
    borderRadius: 16,
    marginBottom: theme.spacing(2.5),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(30, 58, 138, 0.3) 0%, rgba(15, 23, 42, 0.7) 100%)"
      : "linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(59, 130, 246, 0.3)" : "#bfdbfe"}`,
    boxShadow: "0 4px 16px -2px rgba(37, 99, 235, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0 6px 20px -2px rgba(37, 99, 235, 0.15)",
      borderColor: theme.palette.primary.main,
    }
  },
  bannerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 260,
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
    flexShrink: 0,
  },
  bannerInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  bannerTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  bannerTitle: {
    fontWeight: 800,
    fontSize: "1.05rem",
    color: theme.palette.text.primary,
  },
  bannerDetails: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  dateHighlight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 700,
    color: theme.palette.primary.main,
    backgroundColor: safeAlpha(theme.palette.primary.main, 0.08),
    padding: "2px 8px",
    borderRadius: 6,
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  payButton: {
    borderRadius: 12,
    fontWeight: 700,
    textTransform: "none",
    padding: "9px 22px",
    fontSize: "0.92rem",
    boxShadow: "0 4px 14px rgba(25, 118, 210, 0.35)",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 18px rgba(25, 118, 210, 0.45)",
    }
  },

  // Modal Flutuante com Fundo Azul Destacado
  dialogPaper: {
    background: "linear-gradient(145deg, #091a36 0%, #102a5c 50%, #0c2044 100%)",
    color: "#ffffff",
    borderRadius: 20,
    padding: theme.spacing(1),
    border: "1px solid rgba(96, 165, 250, 0.35)",
    boxShadow: "0 25px 60px -15px rgba(2, 6, 23, 0.85), 0 0 50px rgba(59, 130, 246, 0.25)",
    maxWidth: 520,
    width: "100%",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 2, 1, 2),
  },
  dialogTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  dialogIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: "rgba(59, 130, 246, 0.25)",
    border: "1px solid rgba(147, 197, 253, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#60a5fa",
  },
  dialogCloseBtn: {
    color: "rgba(255, 255, 255, 0.7)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    padding: 6,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.18)",
      color: "#ffffff",
    }
  },
  dialogContent: {
    padding: theme.spacing(1.5, 2, 2.5, 2),
    color: "#ffffff",
  },
  summaryBox: {
    background: "rgba(255, 255, 255, 0.06)",
    backdropFilter: "blur(8px)",
    borderRadius: 14,
    border: "1px solid rgba(255, 255, 255, 0.12)",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.88rem",
    color: "rgba(255, 255, 255, 0.85)",
    padding: "4px 0",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: "1px dashed rgba(255, 255, 255, 0.2)",
  },
  totalAmount: {
    fontWeight: 900,
    fontSize: "1.5rem",
    color: "#38bdf8",
    letterSpacing: "-0.5px",
  },
  qrContainer: {
    width: 210,
    height: 210,
    borderRadius: 16,
    background: "#ffffff",
    padding: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "14px auto",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.4)",
  },
  qrImg: {
    width: "100%",
    height: "100%",
    display: "block",
  },
  codeRow: {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
    marginTop: theme.spacing(2),
    flexWrap: "nowrap",
  },
  codeSnippet: {
    wordBreak: "break-all",
    fontFamily: "monospace",
    fontSize: "0.78rem",
    background: "rgba(0, 0, 0, 0.35)",
    color: "#93c5fd",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    padding: "10px 12px",
    borderRadius: 10,
    maxHeight: 75,
    overflow: "auto",
    flex: 1,
    lineHeight: 1.4,
  },
  copyBtn: {
    borderRadius: 10,
    fontWeight: 700,
    textTransform: "none",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    padding: "8px 16px",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#1d4ed8",
    }
  },
  pixInstruction: {
    fontSize: "0.8rem",
    color: "rgba(255, 255, 255, 0.7)",
    textAlign: "center",
    marginTop: theme.spacing(1.5),
    lineHeight: 1.5,
  },
  generateBtn: {
    width: "100%",
    padding: "12px",
    borderRadius: 12,
    fontWeight: 800,
    fontSize: "1rem",
    textTransform: "none",
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    }
  }
}));

export const formatBRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const formatDateBR = (dateOnly) => {
  if (!dateOnly) return "-";
  const [y, m, d] = String(dateOnly).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "-";
};

const describeState = (access) => {
  if (!access) return null;
  if (access.state === "overdue") return { label: "Em atraso", color: "secondary" };
  if (access.state === "warning") {
    return {
      label: access.daysToDue === 0 ? "Vence hoje" : `Vence em ${access.daysToDue} dia(s)`,
      color: "default"
    };
  }
  if (access.state === "active") return { label: "Em dia", color: "primary" };
  return null;
};

/**
 * Painel de pagamento da assinatura em formato de Banner + Modal Flutuante com fundo azul destacado.
 */
const BillingPanel = ({ autoGenerate = false }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [charge, setCharge] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(autoGenerate);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/billing/pix");
      setCharge(data);
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/summary");
        if (!active) return;
        setSummary(data);
        const canPay = data.billingAdmin && data.access?.state !== "not_applicable";
        if (autoGenerate && canPay) {
          generate();
          setModalOpen(true);
        }
      } catch (err) {
        console.error("Erro ao carregar resumo de faturamento:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [autoGenerate, generate]);

  const handleOpenModal = () => {
    setModalOpen(true);
    const canPay = summary?.billingAdmin && summary?.access?.state !== "not_applicable";
    if (canPay && !charge && !generating) {
      generate();
    }
  };

  const copyCode = async () => {
    if (!charge?.pix?.payload) return;
    try {
      await navigator.clipboard.writeText(charge.pix.payload);
      setCopied(true);
      toast.success("Código PIX copiado com sucesso!");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error("Não foi possível copiar. Selecione o código e copie manualmente.");
    }
  };

  if (loading) {
    return (
      <Paper className={classes.bannerCard} variant="outlined">
        <CircularProgress size={24} />
      </Paper>
    );
  }

  // Conta da plataforma (empresa 1) não tem assinatura: o painel não aparece.
  if (!summary || summary.access?.state === "not_applicable") return null;

  const state = describeState(summary.access);
  const canPay = summary.billingAdmin && summary.access?.state !== "not_applicable";

  return (
    <>
      {/* ===== CARD/BANNER FECHADO NA PÁGINA ===== */}
      <Paper className={classes.bannerCard} variant="outlined">
        <div className={classes.bannerLeft}>
          <div className={classes.iconBox}>
            <PaymentIcon />
          </div>
          <div className={classes.bannerInfo}>
            <div className={classes.bannerTitleRow}>
              <Typography className={classes.bannerTitle}>
                Assinatura do sistema
              </Typography>
              {state && (
                <Chip
                  size="small"
                  label={state.label}
                  color={state.color}
                  style={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }}
                />
              )}
            </div>
            <div className={classes.bannerDetails}>
              <span>Plano: <strong>{summary.plan?.name || "-"}</strong></span>
              <span>·</span>
              <span className={classes.dateHighlight}>
                <EventIcon style={{ fontSize: 16 }} />
                Vencimento: {formatDateBR(summary.access?.dueDate)}
              </span>
              <span>·</span>
              <span>Total mensal: <strong>{formatBRL(summary.totalMonthly)}</strong></span>
            </div>
          </div>
        </div>

        <div className={classes.bannerActions}>
          <Button
            variant="contained"
            color="primary"
            className={classes.payButton}
            startIcon={<PaymentIcon />}
            onClick={handleOpenModal}
          >
            Pagar assinatura
          </Button>
        </div>
      </Paper>

      {/* ===== MODAL FLUTUANTE COM FUNDO AZUL DESTACADO ===== */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <div className={classes.dialogHeader}>
          <div className={classes.dialogTitleWrap}>
            <div className={classes.dialogIconBox}>
              <PaymentIcon />
            </div>
            <div>
              <Typography variant="h6" style={{ fontWeight: 800, color: "#ffffff", lineHeight: 1.2 }}>
                Pagar assinatura
              </Typography>
              <Typography variant="caption" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                Pagamento instantâneo via PIX com confirmação automática
              </Typography>
            </div>
          </div>
          <IconButton
            size="small"
            className={classes.dialogCloseBtn}
            onClick={() => setModalOpen(false)}
            aria-label="Fechar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <DialogContent className={classes.dialogContent}>
          {/* Card de Resumo Financeiro */}
          <div className={classes.summaryBox}>
            <div className={classes.summaryRow}>
              <span>Plano Atual</span>
              <strong>{summary.plan?.name || "-"}</strong>
            </div>
            <div className={classes.summaryRow}>
              <span>Data de Vencimento</span>
              <span style={{ color: "#38bdf8", fontWeight: 700 }}>
                {formatDateBR(summary.access?.dueDate)}
              </span>
            </div>
            {state && (
              <div className={classes.summaryRow}>
                <span>Status da Assinatura</span>
                <span style={{
                  color: state.color === "primary" ? "#4ade80" : state.color === "secondary" ? "#f87171" : "#fbbf24",
                  fontWeight: 700
                }}>
                  {state.label}
                </span>
              </div>
            )}

            {(summary.items || []).length > 0 && (
              <>
                <Divider style={{ backgroundColor: "rgba(255, 255, 255, 0.12)", margin: "8px 0" }} />
                {summary.items.map((item) => (
                  <div className={classes.summaryRow} key={item.key}>
                    <span style={{ fontSize: "0.82rem", opacity: 0.85 }}>
                      {item.label}
                      {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                    </span>
                    <span style={{ fontSize: "0.82rem" }}>{formatBRL(item.subtotal)}</span>
                  </div>
                ))}
              </>
            )}

            <div className={classes.totalRow}>
              <Typography style={{ fontWeight: 700, fontSize: "1rem" }}>
                Total a pagar
              </Typography>
              <Typography className={classes.totalAmount}>
                {formatBRL(summary.totalMonthly)}
              </Typography>
            </div>
          </div>

          {!summary.billingAdmin && (
            <Typography style={{ color: "#fca5a5", fontSize: "0.85rem", textAlign: "center", marginBottom: 12 }}>
              Somente o administrador da empresa tem permissão para pagar a assinatura.
            </Typography>
          )}

          {/* Botão de gerar PIX se ainda não foi gerado */}
          {canPay && !charge && (
            <Button
              className={classes.generateBtn}
              disabled={generating}
              onClick={generate}
            >
              {generating ? "Gerando PIX..." : "Gerar QR Code PIX"}
            </Button>
          )}

          {/* Dados do PIX quando gerado */}
          {canPay && charge && (
            <div>
              {charge.pix?.encodedImage && (
                <div className={classes.qrContainer}>
                  <img
                    className={classes.qrImg}
                    alt="QR Code PIX"
                    src={`data:image/png;base64,${charge.pix.encodedImage}`}
                  />
                </div>
              )}

              <div className={classes.codeRow}>
                <div className={classes.codeSnippet}>{charge.pix?.payload}</div>
                <Button
                  className={classes.copyBtn}
                  onClick={copyCode}
                  startIcon={copied ? <CheckIcon /> : <FileCopyIcon />}
                >
                  {copied ? "Copiado!" : "Copiar Código"}
                </Button>
              </div>

              <Typography className={classes.pixInstruction}>
                Pague pelo app do seu banco lendo o QR Code ou utilizando o código Copia e Cola acima.
                A liberação de mais 30 dias é automática e imediata após o pagamento.
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BillingPanel;

